import React, { useState, useEffect, useRef } from 'react';
import { useInsuranceStore } from 'src/store/insurance';
import { OnboardingFormStepLayout } from '@features/onboarding/layouts/OnboardingFormStepLayout';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { transformApplicationData } from '@features/onboarding/utils/transformApplicationData';
import { useFinalSubmissionStore } from 'src/store/final-submission';
import { usePaymentStore } from '@features/onboarding/components/7-finish/store/payment';
import { usePlan } from '@features/onboarding/hooks/usePlan';

import { CoverageReassurance } from './components/coverage-reassurance/CoverageReassurance';
import { CoverageReassuranceWithExcuse } from './components/coverage-reassurance-with-excuse/CoverageReassuranceWithExcuse';
import { ExcuseStep } from './components/excuse-step/ExcuseStep';
import { UnderwritingResult } from './UnderwritingResult';
import { PaymentStep } from './components/stripe-payment/PaymentStep';
import { AddressStep } from './components/address-step/AddressStep';
import { NoPlansSelected } from './components/no-plans-selected/NoPlansSelected';

import { cn } from '@utils/index';
import { getUpdateErrorMessage } from '@utils/errorHandling';
import { UpdateErrorModal } from '@features/onboarding/components/shared/modal/update-error/UpdateErrorModal';
import { FormStepSections } from '@features/onboarding/enums/formSections.enum';
import { useApplicationStore } from 'src/store/application';
import { applicationService } from 'src/services/application';
import { useAIStore } from 'src/store/ai';
import { AI_HOOKS_IDS } from '@features/onboarding/constants';
import { useGeneralStore } from 'src/store';
import {
  getGoogleTrackingKey,
  hasGoogleTracking,
  pushGoogleFormPageUpdate,
} from 'src/hooks/useBranding';
import { validateAgreementSignature } from '@utils/agreementSignature';

const FINISH_FORM_SCREEN_TRACKING: Record<
  string,
  { screenPath: string; screenName: string; pageTitle?: string }
> = {
  coverageReassurance: {
    screenPath: 'finish/review',
    screenName: 'Finish: Review',
    pageTitle: 'Form: Review',
  },
  coverageReassuranceExtraOnly: {
    screenPath: 'finish/review',
    screenName: 'Finish: Review',
    pageTitle: 'Form: Review',
  },
  address: {
    screenPath: 'finish/address',
    screenName: 'Finish: Address and SSN',
    pageTitle: 'Form: Address and SSN',
  },
  payment: {
    screenPath: 'finish/payment',
    screenName: 'Finish: Payment',
    pageTitle: 'Form: Payment',
  },
  noPlansSelected: {
    screenPath: 'finish/no-plans',
    screenName: 'Finish: No Plans Selected',
    pageTitle: 'Form: No Plans Selected',
  },
  allPlansBought: {
    screenPath: 'finish/all-plans-bought',
    screenName: 'Finish: All Plans Bought',
    pageTitle: 'Form: All Plans Bought',
  },
  excuse: {
    screenPath: 'finish/rejected',
    screenName: 'Finish: Rejected',
    pageTitle: 'Form: Rejected',
  },
};

type EmptyStateVariant =
  | 'default'
  | 'browsing'
  | 'maxedOut'
  | 'rejectedCoverage';

type FinishSubStep = 'quote' | 'address' | 'contract' | 'payment';

const components = {
  PaymentStep,
  CoverageReassurance,
  CoverageReassuranceWithExcuse,
  UnderwritingResult,
  ExcuseStep,
  AddressStep,
  NoPlansSelected,
} as any;

const NO_PLANS_COPY: Record<
  EmptyStateVariant,
  { title: string; description: string; helper?: string }
> = {
  default: {
    title: "Oops — you didn't pick a plan.",
    description: "It's like getting to checkout with an empty cart.",
    helper: 'Click the Previous button and choose a plan or two, or three.',
  },
  browsing: {
    title: 'Just browsing? Totally fine.',
    description:
      "Our doors are always open. Come back and explore when you're ready.",
  },
  maxedOut: {
    title: 'You’re maxed out on awesome!',
    description:
      'Looks like you already have every available plan. No need to add more - you’ve reached peak coverage!',
  },
  rejectedCoverage: {
    title: 'You’ve made some wise choices.',
    description:
      'The plans you selected still offer solid benefits and peace of mind.',
  },
};

const DynamicComponent = ({
  componentName,
  handleNextCompatible,
  setContainerClassName,
  handlePrev,
  isSSNValid,
  setIsSSNValid,
  setIsAddressValid,
  addressAttemptedSubmit,
  componentProps = {},
}: any) => {
  const Component = components[componentName];

  // Для PaymentStep передаем onSubmit как handleNextCompatible
  if (componentName === 'PaymentStep') {
    return (
      <Component
        {...componentProps}
        onSubmit={handleNextCompatible}
        setContainerClassName={setContainerClassName}
      />
    );
  }

  if (componentName === 'AddressStep') {
    return (
      <Component
        {...componentProps}
        handleNextCompatible={handleNextCompatible}
        setContainerClassName={setContainerClassName}
        handlePrev={handlePrev}
        onValidationChange={setIsAddressValid}
        attemptedSubmit={addressAttemptedSubmit}
      />
    );
  }

  return (
    <Component
      {...componentProps}
      handleNextCompatible={handleNextCompatible}
      setContainerClassName={setContainerClassName}
      handlePrev={handlePrev}
    />
  );
};

export const Finish = () => {
  const {
    data,
    setFinishStepKey,
    finishSubStep: subStep,
    setFinishSubStep: setSubStep,
  } = useOnboardingStore();
  const { isLocalLoading, handlePreviousStep, handleJumpTo } =
    useFormControls();
  const { plans: chosenPlans } = usePlan();
  const plans = useInsuranceStore((state) => state.plans);
  const { setCurrentPage } = useAIStore();
  const tracking = useGeneralStore((state) => state.siteConfig?.tracking);
  const trackingKey = getGoogleTrackingKey(tracking);

  const [isOkReject, setIsOkReject] = useState(false);
  const [isSSNValid, setIsSSNValid] = useState(false);
  const [isAddressValid, setIsAddressValid] = useState(false);
  const [addressAttemptedSubmit, setAddressAttemptedSubmit] = useState(0);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const addressSavedRef = useRef(false);
  const trackedFinishScreenRef = useRef<string | null>(null);
  const agreementPaymentFlowRef = useRef(false);
  const {
    signedAt,
    signature,
    activeSubscriptionPlans,
    isAllRejected,
    isMainPlanRejected,
    activeCategories,
    applications,
    hasCompletedApplication,
  } = useApplicationStore();

  // Legacy DocuSign applications that were already signed still go directly to payment.
  const paymentOnlyCondition = !!(
    signedAt &&
    signature &&
    !hasCompletedApplication
  );

  const medicalPlans = React.useMemo(
    () => chosenPlans.filter((plan) => plan.baseKey === 'majormedical'),
    [chosenPlans]
  );
  const otherPlans = React.useMemo(
    () => chosenPlans.filter((plan) => plan.baseKey !== 'majormedical'),
    [chosenPlans]
  );
  const firstName = React.useMemo(() => {
    const primary = data?.applicants?.find((a) => a.applicant === 'primary');
    return primary?.firstName || '';
  }, [data]);
  const hasSelectedMedicalPlan = medicalPlans.length > 0;
  const hasOtherSelectedPlans = otherPlans.length > 0;
  const {
    isUnderwritingLoading,
    underwritingResult,
    isSubmittingApplication,
    submitFinalApplication,
    submissionError,
    isSubmitted,
  } = useFinalSubmissionStore();
  const [isUpdateApplicationLoading, setIsUpdateApplicationLoading] =
    useState(false);

  const {
    paymentMethodId,
    resetPaymentMethodId,
    contractSignatureName,
    resetContractSignatureName,
  } = usePaymentStore();

  // Ref to track if final submission has been initiated to prevent duplicate calls
  const hasInitiatedSubmissionRef = useRef(false);

  // Reset the submission ref when paymentMethodId is cleared (allows retry)
  useEffect(() => {
    if (!paymentMethodId) {
      hasInitiatedSubmissionRef.current = false;
    }
  }, [paymentMethodId]);

  const hasAnyPlan = !!chosenPlans.length;
  const requiresAgreementSignature = hasSelectedMedicalPlan && !signedAt;
  const contractSignatureValidation = React.useMemo(
    () => validateAgreementSignature(contractSignatureName),
    [contractSignatureName]
  );
  const isPaymentFinalizing =
    Boolean(paymentMethodId) || isSubmittingApplication || isSubmitted;
  useEffect(() => {
    if (requiresAgreementSignature) {
      agreementPaymentFlowRef.current = true;
      return;
    }

    if (!isPaymentFinalizing) {
      agreementPaymentFlowRef.current = false;
    }
  }, [isPaymentFinalizing, requiresAgreementSignature]);

  useEffect(() => {
    if (
      hasSelectedMedicalPlan ||
      isPaymentFinalizing ||
      !contractSignatureName
    ) {
      return;
    }

    resetContractSignatureName();
  }, [
    contractSignatureName,
    hasSelectedMedicalPlan,
    isPaymentFinalizing,
    resetContractSignatureName,
  ]);

  const shouldShowAgreementPayment =
    requiresAgreementSignature ||
    (contractSignatureValidation.isValid && isPaymentFinalizing) ||
    (agreementPaymentFlowRef.current && isPaymentFinalizing);
  const hasActiveSubscriptions = React.useMemo(
    () => (activeSubscriptionPlans?.length ?? 0) > 0,
    [activeSubscriptionPlans]
  );
  const noPlansVariant = React.useMemo<EmptyStateVariant>(() => {
    if (!hasAnyPlan) {
      if (isAllRejected && !isMainPlanRejected) {
        return 'maxedOut';
      }
      if (isMainPlanRejected) {
        const allCategories = [...new Set(plans.map((p) => p.category))];
        const nonMedicalCategories = allCategories.filter(
          (cat) => cat !== 'Major Medical'
        );
        const allOtherPlansPurchased =
          nonMedicalCategories.length > 0 &&
          nonMedicalCategories.every((cat) => activeCategories.includes(cat));

        if (allOtherPlansPurchased) {
          return 'rejectedCoverage';
        }
      }

      if (
        (underwritingResult?.status === 'rejected' || isMainPlanRejected) &&
        hasActiveSubscriptions
      ) {
        return 'rejectedCoverage';
      }
      if (hasActiveSubscriptions) {
        return 'browsing';
      }
    }

    return 'default';
  }, [
    hasAnyPlan,
    hasActiveSubscriptions,
    isAllRejected,
    underwritingResult?.status,
    isMainPlanRejected,
    activeCategories,
    plans,
  ]);
  const noPlansCopy = NO_PLANS_COPY[noPlansVariant];

  // Set current page for AI hooks when maxedOut variant is shown
  useEffect(() => {
    if (noPlansVariant === 'maxedOut') {
      setTimeout(() => {
        setCurrentPage(AI_HOOKS_IDS.MAXED_OUT);
      }, 200);
    }
  }, [noPlansVariant, setCurrentPage]);

  const medicalPriceInfo = React.useMemo(() => {
    if (!hasSelectedMedicalPlan) return null;
    const selectedMedical = medicalPlans[0];
    // выбранный план в списке всех планов по uid
    const fullPlan = plans.find((p) => p.uid === (selectedMedical as any)?.id);
    if (!fullPlan) return null;
    const originalPrice = fullPlan.averagePrice;
    const currentPrice = (selectedMedical as any)?.price ?? 0;
    return {
      originalPrice,
      currentPrice,
      isHigherPrice: currentPrice > originalPrice,
    };
  }, [hasSelectedMedicalPlan, medicalPlans, plans]);

  // On mount or when signedAt changes: legacy signed applications jump to payment.
  useEffect(() => {
    if (paymentOnlyCondition) {
      setSubStep('payment');
    }
  }, [paymentOnlyCondition]);

  // Async fallback: check server data on mount if store doesn't have signedAt
  useEffect(() => {
    if (paymentOnlyCondition) return;
    const checkPaymentOnlyCondition = async () => {
      try {
        const applicationData = await applicationService.getApplications();
        const shouldGoToPayment = !!(
          applicationData?.signature?.signedAt &&
          applicationData?.signature?.signatureRequestId &&
          applicationData.status === 0
        );
        if (shouldGoToPayment) {
          setSubStep('payment');
        }
      } catch (error) {
        console.error('Failed to check payment only condition:', error);
      }
    };
    checkPaymentOnlyCondition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!paymentMethodId) return;
    if (requiresAgreementSignature && !contractSignatureValidation.isValid) {
      console.warn(
        '[Finish] Submission blocked: agreement signature is invalid',
        contractSignatureValidation.error
      );
      return;
    }

    // Prevent multiple submissions - check if already submitting, already initiated, or already submitted
    if (
      isSubmittingApplication ||
      hasInitiatedSubmissionRef.current ||
      isSubmitted
    ) {
      console.log(
        '[Finish] Submission blocked: already in progress, initiated, or submitted',
        {
          isSubmittingApplication,
          hasInitiatedSubmission: hasInitiatedSubmissionRef.current,
          isSubmitted,
        }
      );
      return;
    }

    if (
      underwritingResult?.status === 'rejected' &&
      !isOkReject &&
      !hasOtherSelectedPlans
    )
      return;

    // Mark as initiated before async call to prevent race conditions
    hasInitiatedSubmissionRef.current = true;
    console.log('[Finish] Starting final submission, marked as initiated');

    const updatedFormData = useOnboardingStore.getState().data;
    const applicationData = transformApplicationData(updatedFormData, plans);

    const filteredApplicationData = (() => {
      if (underwritingResult?.status !== 'rejected') return applicationData;

      const insurancePlanById = new Map<number, (typeof plans)[number]>(
        plans
          .filter((p) => typeof p.id === 'number')
          .map((p) => [p.id as number, p])
      );

      const nonMedicalPlans = applicationData.plans.filter((p) => {
        const src = insurancePlanById.get(p.id);
        return src ? src.category !== 'Major Medical' : true;
      });

      return { ...applicationData, plans: nonMedicalPlans };
    })();

    const startFinalRequest = async (token: string) => {
      try {
        await submitFinalApplication(
          filteredApplicationData,
          token,
          requiresAgreementSignature
            ? contractSignatureValidation.value
            : undefined
        );
        // Only navigate if submission was successful (no error thrown)
        console.log(
          '[Finish] Application submitted successfully, navigating to thanks page'
        );
        window.location.replace('/thanks');
      } catch (error) {
        console.error('[Finish] Error submitting application:', error);
        // Reset the ref on error so user can retry
        hasInitiatedSubmissionRef.current = false;
        // Error toast is already shown in store, don't navigate on error - let user retry
        // Make sure we don't navigate on error
        return;
      }
    };

    startFinalRequest(paymentMethodId);
  }, [
    paymentMethodId,
    isOkReject,
    underwritingResult?.status,
    plans,
    submitFinalApplication,
    hasOtherSelectedPlans,
    isSubmittingApplication,
    isSubmitted,
    requiresAgreementSignature,
    contractSignatureValidation.error,
    contractSignatureValidation.isValid,
    contractSignatureValidation.value,
  ]);

  type FinishUI = {
    title?: string;
    description?: string;
    nextButtonLabel?: string;
    hideNextButton?: boolean;
    nextAction?: 'GO_TO_ADDRESS' | 'GO_TO_PAYMENT' | 'SUBMIT';
    key?: string;
    componentName:
      | 'PaymentStep'
      | 'CoverageReassurance'
      | 'CoverageReassuranceWithExcuse'
      | 'UnderwritingResult'
      | 'ExcuseStep'
      | 'AddressStep'
      | 'NoPlansSelected';
    componentProps?: Record<string, unknown>;
  };
  const ui: FinishUI = React.useMemo(() => {
    const paymentUi: FinishUI = {
      title: shouldShowAgreementPayment
        ? 'Agreement and Payment'
        : 'Securely connect & pay.',
      description: shouldShowAgreementPayment
        ? 'Review your agreement and enter your payment information to complete the application process.'
        : "In this step, you'll set up your ACH payment by linking your bank account through our secure system. It's fast, safe, and once connected, future payments are automatic — no extra steps needed.",
      nextButtonLabel: 'Submit',
      hideNextButton: true,
      componentName: 'PaymentStep',
      key: 'payment',
      componentProps: {
        isSubmitting: isSubmittingApplication,
        submissionError,
        requiresAgreement: shouldShowAgreementPayment,
      },
    };

    // Payment step - when paymentMethodId is set (submitting) or subStep is 'payment'
    if (paymentMethodId) {
      return paymentUi;
    }

    if (subStep === 'payment' || subStep === 'contract') {
      return paymentUi;
    }

    // Handle case when no plans are selected
    if (!hasAnyPlan) {
      return {
        title: noPlansCopy.title,
        description: noPlansCopy.description,
        nextButtonLabel: undefined,
        hideNextButton: true,
        componentName: 'NoPlansSelected',
        key:
          noPlansVariant === 'rejectedCoverage'
            ? 'allPlansBought'
            : 'noPlansSelected',
        componentProps: { copy: noPlansCopy },
      };
    }

    // Address step — shared across all underwriting statuses
    if (subStep === 'address') {
      return {
        title: 'Verify your identity securely.',
        description:
          'We take your privacy seriously. Your address and SSN(s) help us verify your identity and prevent fraud. <strong>Your data is encrypted in transit and at rest, strictly access-controlled, and never sold.</strong>',
        nextButtonLabel: 'Next',
        hideNextButton: false,
        componentName: 'AddressStep',
        key: 'address',
      };
    }
    if (
      underwritingResult?.status === 'accepted' ||
      underwritingResult?.status === undefined
    ) {
      // Quote step (CoverageReassurance) - subStep === 'quote'
      // Only extra plans (no health)
      if (!hasSelectedMedicalPlan && hasOtherSelectedPlans) {
        return {
          title: 'Let\u2019s review things.',
          description: `Here's a quick look at what you've chosen. Take a moment to confirm everything looks right before moving on.
          Once you're ready, the next step is simple: just set up your payment, and you'll be done!`,
          nextButtonLabel: 'Next',
          hideNextButton: false,
          componentName: 'CoverageReassurance',
          key: 'coverageReassuranceExtraOnly',
        };
      }
      if (hasSelectedMedicalPlan && hasOtherSelectedPlans && !signedAt) {
        const descriptionMedicalPlan = `Here's a quick look at what you've chosen. Take a moment to confirm everything looks right before moving on.
        Once you're ready, the next steps are simple: review your agreement and set up your payment, and you'll be done!`;

        return {
          title: 'Let\u2019s review things.',
          description: descriptionMedicalPlan,
          nextButtonLabel: 'Next',
          nextAction: 'GO_TO_ADDRESS',
          hideNextButton: false,
          componentName: 'CoverageReassurance',
          key: 'coverageReassurance',
        };
      }
      if (hasSelectedMedicalPlan && !hasOtherSelectedPlans && !signedAt) {
        const descriptionMedicalPlan = `Here's a quick look at what you've chosen. Take a moment to confirm everything looks right before moving on.
        Once you're ready, the next steps are simple: review your agreement and set up your payment, and you'll be done!`;
        const descriptionOtherPlans = `Here's a quick look at what you've chosen. Take a moment to confirm everything looks right before moving on.
        Once you're ready, the next step is simple: just set up your payment, and you'll be done!`;

        return {
          title: 'Let\u2019s review things.',
          description: hasSelectedMedicalPlan
            ? descriptionMedicalPlan
            : descriptionOtherPlans,
          nextButtonLabel: 'Next',
          nextAction: 'GO_TO_ADDRESS',
          hideNextButton: false,
          componentName: 'CoverageReassurance',
          key: 'coverageReassurance',
        };
      }
    }

    if (underwritingResult?.status === 'rejected') {
      // Rejected health but kept extra plans — same flow as extra-only (no contract)
      if (hasOtherSelectedPlans) {
        return {
          title: "You've made some wise choices.",
          description:
            'The plans you selected still offer solid benefits and peace of mind.',
          nextButtonLabel: 'Next',
          nextAction: 'GO_TO_ADDRESS' as const,
          hideNextButton: false,
          componentName: 'CoverageReassurance',
          key: 'coverageReassuranceExtraOnly',
        };
      }

      return {
        title: 'You Still Have a Path to Coverage!',
        description:
          "We can't offer you one of our health plans this time, but that doesn't mean the search is over.\n\n" +
          'You may qualify for an ACA plan...',
        nextButtonLabel: undefined,
        hideNextButton: true,
        componentName: 'ExcuseStep',
        key: 'excuse',
      };
    }

    return {
      title: "We're finalizing your quote.",
      description:
        "Once you\u2019re ready, the next steps are simple: review your agreement and set up your payment, and you'll be done!",
      nextButtonLabel:
        hasSelectedMedicalPlan && !signedAt ? 'Continue' : 'Payment',
      hideNextButton: false,
      componentName: 'CoverageReassurance',
      key: 'coverageReassurance',
    };
  }, [
    subStep,
    underwritingResult,
    hasSelectedMedicalPlan,
    hasOtherSelectedPlans,
    signedAt,
    paymentMethodId,
    isSubmittingApplication,
    submissionError,
    hasAnyPlan,
    noPlansCopy,
    requiresAgreementSignature,
    shouldShowAgreementPayment,
  ]);

  const {
    title,
    description,
    nextButtonLabel,
    hideNextButton,
    componentName,
    key,
    componentProps,
  } = ui;
  const { currentApplicationId, updateApplicationById } = useApplicationStore();

  useEffect(() => {
    const screen = key ? FINISH_FORM_SCREEN_TRACKING[key] : undefined;
    if (!screen || !hasGoogleTracking(tracking)) return;

    const screenKey = `${trackingKey}:${screen.screenPath}`;
    if (trackedFinishScreenRef.current === screenKey) return;

    pushGoogleFormPageUpdate(tracking, screen);
    trackedFinishScreenRef.current = screenKey;
  }, [key, tracking, trackingKey]);

  useEffect(() => {
    setFinishStepKey(key ?? null);
    return () => {
      setFinishStepKey(null);
    };
  }, [key, setFinishStepKey]);

  // Reset finishSubStep when leaving the Finish step so that returning
  // (e.g. after AI navigates to a plan page) starts from the quote sub-step
  useEffect(() => {
    return () => {
      setSubStep('quote');
    };
  }, []);

  useEffect(() => {
    if (subStep === 'address' && currentApplicationId) {
      applicationService.updateAddressProgress(currentApplicationId);
    }
  }, [subStep, currentApplicationId]);

  // Helper: determine the next sub-step after address
  const getNextStepAfterAddress = (): FinishSubStep => {
    return 'payment';
  };

  const handlePrev = (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();
    setAddressAttemptedSubmit(0);

    switch (subStep) {
      case 'payment':
        resetPaymentMethodId();
        // If a legacy agreement is already signed, exit Finish entirely.
        if (signedAt) {
          setSubStep('quote');
          handlePreviousStep();
          return;
        }
        // Go back: payment -> address. DocuSign/contract page is no longer used.
        setSubStep('address');
        return;

      case 'contract':
        // Legacy contract sub-step -> address
        setSubStep('address');
        return;

      case 'address':
        // address -> quote
        setSubStep('quote');
        return;

      case 'quote':
      default:
        if (underwritingResult?.status === 'rejected') {
          useOnboardingStore.getState().removeMedicalPlan();
        }
        if (
          underwritingResult?.status === 'rejected' &&
          !hasOtherSelectedPlans
        ) {
          handleJumpTo(FormStepSections.Dental);
          return;
        }
        handlePreviousStep();
        return;
    }
  };

  const handleNextCompatible = async (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();

    // Address step: validate, save, then go to payment
    if (componentName === 'AddressStep') {
      setAddressAttemptedSubmit(addressAttemptedSubmit + 1);

      if (!isAddressValid) {
        console.log('Address form is not valid, cannot proceed');
        return;
      }

      // Skip API calls if address was already saved (e.g. user went back and forward)
      if (!addressSavedRef.current) {
        setIsUpdateApplicationLoading(true);
        try {
          if (currentApplicationId && applications[0]) {
            await updateApplicationById(currentApplicationId, applications[0]);
          }
        } catch (error) {
          console.error('Error updating application:', error);
          setUpdateError(getUpdateErrorMessage(error));
          setIsUpdateApplicationLoading(false);
          return;
        }
        setIsUpdateApplicationLoading(false);
        addressSavedRef.current = true;
      }

      setAddressAttemptedSubmit(0);

      // Always go forward: address -> payment. Agreement is signed on the payment step.
      setSubStep(getNextStepAfterAddress());
      return;
    }

    // Quote step: handle nextAction or default navigation
    switch (ui.nextAction) {
      case 'GO_TO_ADDRESS':
        if (
          underwritingResult?.status === 'rejected' &&
          hasOtherSelectedPlans
        ) {
          setIsOkReject(true);
        }
        setSubStep('address');
        return;
      case 'GO_TO_PAYMENT':
        setSubStep('payment');
        return;
    }

    // CoverageReassurance without nextAction -> go to address
    if (componentName === 'CoverageReassurance' && !signedAt) {
      setSubStep('address');
      return;
    }

    // Rejected with accepted other plans -> payment
    if (underwritingResult?.status === 'rejected' && isOkReject) {
      setSubStep('payment');
      return;
    }
  };

  const shouldShowSubmitText = () => {
    if (isUnderwritingLoading || isSubmittingApplication) return false;

    if (!hasAnyPlan) {
      return subStep === 'payment';
    }

    return false;
  };

  useEffect(() => {
    setIsOkReject(false);
  }, []);

  console.log('componentName', componentName);

  return (
    <OnboardingFormStepLayout
      handleFormSubmit={handleNextCompatible}
      onPrev={handlePrev}
      title={title}
      description={description}
      hideNextButton={hideNextButton}
      isSubmitText={!!shouldShowSubmitText()}
      onlyImage={false}
      // isLocalLoading - TODO не меняется PV-261
      isLoading={
        isUnderwritingLoading ||
        isSubmittingApplication ||
        isLocalLoading ||
        isUpdateApplicationLoading ||
        !!paymentMethodId
      }
      nextButtonLabel={nextButtonLabel}
      headerAnimationKey={key}
      idStep={key ? `Finish-${key}` : 'Finish'}
    >
      <div className={cn('h-full w-full rounded-2xl')}>
        <div className="pb-4 h-full w-full justify-center">
          <DynamicComponent
            handlePrev={handlePrev}
            handleNextCompatible={handleNextCompatible}
            componentName={componentName}
            componentProps={componentProps}
            isSSNValid={isSSNValid}
            setIsSSNValid={setIsSSNValid}
            setIsAddressValid={setIsAddressValid}
            addressAttemptedSubmit={addressAttemptedSubmit}
          />
        </div>
      </div>
      <UpdateErrorModal
        isOpen={!!updateError}
        message={updateError || ''}
        onClose={() => setUpdateError(null)}
      />
    </OnboardingFormStepLayout>
  );
};
